/**
 * Data Flow Manager - Main Component
 * Visual data flow orchestration interface
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useEdgesState,
  useNodesState,
  type Node as ReactFlowNode,
  type Edge as ReactFlowEdge,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  listFlows,
  getFlow,
  createFlow,
  deleteFlow,
  updateFlow,
  executeFlow,
  listSystemFlows,
  listDataflowPipelines,
  createDataflowPipeline,
  listExternalApis,
  runIngestPipeline,
  deleteIngestPipeline,
  getFlowActivity,
  getExecutionEvents,
  getSystemFlowOverride,
  createSystemFlowOverride,
  updateSystemFlowOverride,
  deleteSystemFlowOverride,
} from './api';
import DataFlowRoutingBoard from './routingBoard/DataFlowRoutingBoard';
import type {
  Flow,
  FlowNode,
  FlowEdge,
  ExternalApi,
  ApiEndpoint,
  SystemFlow,
  DataflowPipeline,
  PipelineType,
  TelemetryActivity,
  FlowRunEvent,
  FlowEventStatus,
} from './types';
import { NODE_LABELS } from './types';
import { canonicalizeSystemFlowRouteId, matchSystemFlow, systemFlowListQuery } from './systemFlowId';
import { useAppStore } from '@/store/appStore';
import { buildEventSourcePath } from '@/lib/eventSourceAuth';

const SYSTEM_FLOW_PREFIX = 'system-';
const EDGE_PULSE_MS = 2000;
const LOG_LIMIT = 200;
const BIDIRECTIONAL_NODE_TYPES = new Set<string>([
  'http_api',
  'api_endpoint',
  'database',
  'mongodb',
  'timescaledb',
  'webhook',
  'webhook_call',
]);

function isSystemFlowId(id?: string) {
  return Boolean(id && id.startsWith(SYSTEM_FLOW_PREFIX));
}

function normalizeSystemFlow(flow: SystemFlow): SystemFlow {
  if (flow.flow_type && flow.entity) return flow;
  const meta = (flow.metadata || {}) as any;
  const derivedType = flow.flow_type
    || (meta.category === 'api_ingest' ? 'api_ingest'
      : meta.category === 'public_api' ? 'public_api'
        : meta.category === 'iot_shack' ? 'device_telemetry'
          : meta.category === 'sensor_type' ? 'device_model_telemetry'
            : meta.category === 'sensor' ? 'device_telemetry'
              : meta.category === 'pipeline' ? 'pipeline'
                : undefined);
  const derivedEntity = flow.entity || (() => {
    if (meta.deviceLabel || meta.deviceId) {
      return { type: 'device', id: meta.deviceId || meta.imei || meta.deviceLabel, label: meta.deviceLabel || meta.deviceId };
    }
    if (meta.pipeline_id) {
      return { type: 'pipeline', id: meta.pipeline_id, label: meta.pipeline_id };
    }
    if (meta.endpoint_id) {
      return { type: 'api_endpoint', id: meta.endpoint_id, label: meta.endpoint_id };
    }
    if (meta.consumer_id) {
      return { type: 'app', id: meta.consumer_id, label: meta.consumer_id };
    }
    return null;
  })();
  return {
    ...flow,
    flow_type: derivedType || flow.flow_type,
    entity: derivedEntity || flow.entity || null,
  };
}

const STATUS_STYLES: Record<FlowEventStatus, string> = {
  started: 'bg-blue-100 text-blue-700',
  running: 'bg-blue-100 text-blue-700',
  success: 'bg-green-100 text-green-700',
  error: 'bg-red-100 text-red-700',
  queued: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

function toDisplayNodes(nodes: FlowNode[], nodeStatus: Record<string, FlowRunEvent>): ReactFlowNode[] {
  return nodes.map((node) => ({
    ...node,
    type: 'default',
    data: {
      ...node.data,
      label: (
        <div className="text-sm leading-tight">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">
            {NODE_LABELS[node.type] || node.type}
          </div>
          <div className="font-semibold text-gray-900">{node.data?.label || node.id}</div>
          {nodeStatus[node.id]?.status ? (
            <div className="mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_STYLES[nodeStatus[node.id].status]}`}>
                {nodeStatus[node.id].status}
              </span>
            </div>
          ) : null}
          {node.data?.description ? (
            <div className="text-[11px] text-gray-500">{node.data.description}</div>
          ) : null}
          {nodeStatus[node.id]?.error ? (
            <div className="text-[11px] text-red-600 mt-1">{nodeStatus[node.id].error}</div>
          ) : null}
        </div>
      )
    }
  }));
}

function toDisplayEdges(
  edges: FlowEdge[],
  nodeLookup: Record<string, FlowNode>,
  edgeStatus: Record<string, FlowRunEvent>,
  edgePulse: Record<string, FlowRunEvent>,
  bidirectionalNodeTypes: Set<string>
): ReactFlowEdge[] {
  return edges.map((edge, index) => {
    const edgeId = edge.id || `e-${edge.source}-${edge.target}-${index}`;
    const status = edgeStatus[edgeId]?.status;
    const color =
      status === 'error'
        ? '#dc2626'
        : status === 'success'
          ? '#16a34a'
          : status === 'running'
            ? '#2563eb'
            : '#9ca3af';
    const sourceNode = nodeLookup[edge.source];
    const targetNode = nodeLookup[edge.target];
    const bidirectional =
      (sourceNode && bidirectionalNodeTypes.has(sourceNode.type)) ||
      (targetNode && bidirectionalNodeTypes.has(targetNode.type));
    return {
      ...edge,
      id: edgeId,
      type: edge.type || 'smoothstep',
      animated: Boolean(edgePulse[edgeId]),
      style: {
        stroke: color,
        strokeWidth: 2,
      },
      markerEnd: { type: MarkerType.ArrowClosed, color },
      markerStart: bidirectional ? { type: MarkerType.ArrowClosed, color } : undefined,
    };
  });
}


function LiveDataCard({ event }: { event: { id: number; raw: any; iku: any; endpoint_id?: string; ts: number } }) {
  const [expanded, setExpanded] = React.useState(false);
  const time = new Date(event.ts).toLocaleTimeString();
  const rawText = typeof event.raw === 'string' ? event.raw : JSON.stringify(event.raw, null, 2);
  const ikuText = typeof event.iku === 'string' ? event.iku : JSON.stringify(event.iku, null, 2);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 transition"
      >
        <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
        <span className="text-gray-500">{time}</span>
        {event.endpoint_id && <span className="text-gray-700 font-medium truncate">{event.endpoint_id}</span>}
        <span className="ml-auto text-gray-400">{expanded ? '\u25B2' : '\u25BC'}</span>
      </button>
      {expanded && (
        <div className="border-t">
          <div className="grid grid-cols-2 divide-x">
            <div className="p-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Raw Payload</div>
              <pre className="text-[11px] font-mono bg-gray-50 rounded p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">{rawText}</pre>
            </div>
            <div className="p-2">
              <div className="text-[10px] uppercase tracking-wide text-green-700 font-semibold mb-1">Mapped IKU</div>
              <pre className="text-[11px] font-mono bg-green-50 rounded p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">{ikuText}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DataFlowManager() {
  const { flowId } = useParams<{ flowId?: string }>();
  const navigate = useNavigate();
  const { appId, appName } = useAppStore();
  const [flows, setFlows] = useState<Flow[]>([]);
  const [currentFlow, setCurrentFlow] = useState<Flow | null>(null);
  const [systemFlow, setSystemFlow] = useState<SystemFlow | null>(null);
  const [systemFlows, setSystemFlows] = useState<SystemFlow[]>([]);
  const [pipelines, setPipelines] = useState<DataflowPipeline[]>([]);
  const [externalApis, setExternalApis] = useState<ExternalApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [systemLoading, setSystemLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showNewFlowDialog, setShowNewFlowDialog] = useState(false);
  const [showPipelineDialog, setShowPipelineDialog] = useState(false);
  const [showEditFlowDialog, setShowEditFlowDialog] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [pipelineStep, setPipelineStep] = useState(0);
  const [pipelineType, setPipelineType] = useState<PipelineType>('api_ingest');
  const [pipelineName, setPipelineName] = useState('');
  const [pipelineDescription, setPipelineDescription] = useState('');
  const [pipelineSpecJson, setPipelineSpecJson] = useState('{}');
  const [pipelineApiId, setPipelineApiId] = useState('');
  const [pipelineEndpointId, setPipelineEndpointId] = useState('');
  const [pipelineEverySeconds, setPipelineEverySeconds] = useState(300);
  const [pipelineIngestMode, setPipelineIngestMode] = useState<'pulsar' | 'timescale' | 'dual'>('pulsar');
  const [pipelineStoreMode, setPipelineStoreMode] = useState<'telemetry' | 'api_telemetry' | 'both' | 'pim'>('telemetry');
  const [systemCategory, setSystemCategory] = useState('all');
  const [systemDeviceId, setSystemDeviceId] = useState('');
  const [systemSensorType, setSystemSensorType] = useState('');
  const [systemFlowType, setSystemFlowType] = useState('');
  const [systemEntityType, setSystemEntityType] = useState('');
  const [systemEntityId, setSystemEntityId] = useState('');
  const [systemSourceSystem, setSystemSourceSystem] = useState('');
  const [showAllUserFlows, setShowAllUserFlows] = useState(false);
  const [activity, setActivity] = useState<TelemetryActivity[]>([]);
  const [lastActivityAt, setLastActivityAt] = useState<string | null>(null);
  const [editFlowName, setEditFlowName] = useState('');
  const [editFlowDescription, setEditFlowDescription] = useState('');
  const [overrideName, setOverrideName] = useState('');
  const [overrideDescription, setOverrideDescription] = useState('');
  const [overrideNodesJson, setOverrideNodesJson] = useState('[]');
  const [overrideEdgesJson, setOverrideEdgesJson] = useState('[]');
  const [overrideMetadataJson, setOverrideMetadataJson] = useState('{}');
  const [overrideId, setOverrideId] = useState<string | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<ReactFlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<ReactFlowEdge>([]);
  const [eventLog, setEventLog] = useState<FlowRunEvent[]>([]);
  const [nodeStatus, setNodeStatus] = useState<Record<string, FlowRunEvent>>({});
  const [edgeStatus, setEdgeStatus] = useState<Record<string, FlowRunEvent>>({});
  const [edgePulse, setEdgePulse] = useState<Record<string, FlowRunEvent>>({});
  const [activeExecutionId, setActiveExecutionId] = useState<string | null>(null);
  const [activePipelineRunId, setActivePipelineRunId] = useState<string | null>(null);
  const [streamState, setStreamState] = useState<{ dataflow: string; pipeline: string }>({
    dataflow: 'idle',
    pipeline: 'idle',
  });
  const [logNodeFilter, setLogNodeFilter] = useState<string | null>(null);
  const [liveDataEvents, setLiveDataEvents] = useState<{ id: number; raw: any; iku: any; endpoint_id?: string; ts: number }[]>([]);
  const [infoPanelTab, setInfoPanelTab] = useState<'info' | 'livedata'>('info');
  const liveDataIdRef = useRef(0);
  const dataflowStreamRef = useRef<EventSource | null>(null);
  const pipelineStreamRef = useRef<EventSource | null>(null);

  const activeFlow = systemFlow || currentFlow;
  const nodeLookup = useMemo(() => {
    const lookup: Record<string, FlowNode> = {};
    (activeFlow?.nodes || []).forEach((node) => {
      lookup[node.id] = node;
    });
    return lookup;
  }, [activeFlow]);
  const edgeKeyLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    (activeFlow?.edges || []).forEach((edge, index) => {
      const edgeId = edge.id || `e-${edge.source}-${edge.target}-${index}`;
      lookup[`${edge.source}->${edge.target}`] = edgeId;
    });
    return lookup;
  }, [activeFlow]);



  // Load flows list
  useEffect(() => {
    if (appId) {
      loadFlows();
    }
  }, [appId, showAllUserFlows]);

  // Load system flows (all sources) for list view
  useEffect(() => {
    if (!flowId) {
      loadSystemFlows();
      loadPipelines();
    }
  }, [flowId, systemCategory, systemDeviceId, systemSensorType, systemFlowType, systemEntityType, systemEntityId, systemSourceSystem]);

  // Load specific flow if flowId provided
  useEffect(() => {
    if (flowId) {
      loadFlowDetail(flowId);
    }
  }, [flowId]);

  useEffect(() => {
    if (flowId && isSystemFlowId(flowId)) {
      const match = matchSystemFlow(systemFlows, flowId);
      if (match) setSystemFlow(match);
    }
  }, [flowId, systemFlows]);

  // Sync ReactFlow nodes/edges for active flow
  useEffect(() => {
    if (!activeFlow) return;
    setNodes(toDisplayNodes(activeFlow.nodes || [], nodeStatus));
    setEdges(toDisplayEdges(activeFlow.edges || [], nodeLookup, edgeStatus, edgePulse, BIDIRECTIONAL_NODE_TYPES));
  }, [activeFlow, nodeStatus, edgeStatus, edgePulse, nodeLookup, setNodes, setEdges]);

  useEffect(() => {
    if (!activeFlow) return;
    let isMounted = true;
    let timer: ReturnType<typeof setInterval> | null = null;

    const tick = async () => {
      try {
        const deviceId = (activeFlow.metadata as any)?.deviceId || undefined;
        const items = await getFlowActivity({ deviceId, limit: 5 });
        if (!isMounted) return;
        if (items.length > 0) {
          const now = new Date().toISOString();
          setActivity(items);
          setLastActivityAt(now);
          const telemetryEvent: FlowRunEvent = {
            source: 'telemetry',
            kind: 'telemetry',
            status: 'success',
            ts: now,
            detail: { count: items.length },
          };
          setEventLog((prev) => [telemetryEvent, ...prev].slice(0, LOG_LIMIT));
          setEdgePulse(() => {
            const next: Record<string, FlowRunEvent> = {};
            (activeFlow.edges || []).forEach((edge, index) => {
              const edgeId = edge.id || `e-${edge.source}-${edge.target}-${index}`;
              next[edgeId] = telemetryEvent;
            });
            return next;
          });
          setTimeout(() => {
            setEdgePulse({});
          }, EDGE_PULSE_MS);
        }
      } catch {
        // ignore
      }
    };

    tick();
    timer = setInterval(tick, 5000);
    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [activeFlow]);

  const loadFlows = async () => {
    try {
      setLoading(true);
      const data = await listFlows({ limit: 100, scope: showAllUserFlows ? 'all' : undefined });
      setFlows(data.flows);
    } catch (error) {
      console.error('Failed to load flows:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemFlows = async () => {
    try {
      setSystemLoading(true);
      const [systemData, apisData] = await Promise.all([
        listSystemFlows({
          category: systemCategory !== 'all' ? systemCategory : undefined,
          deviceId: systemDeviceId || undefined,
          sensorType: systemSensorType || undefined,
          flowType: systemFlowType || undefined,
          entityType: systemEntityType || undefined,
          entityId: systemEntityId || undefined,
          sourceSystem: systemSourceSystem || undefined,
          scope: showAllUserFlows ? 'all' : undefined,
          limit: 50,
        }),
        listExternalApis({ limit: 200 })
      ]);
      setSystemFlows((systemData || []).map(normalizeSystemFlow));
      setExternalApis(apisData || []);
    } catch (error) {
      console.error('Failed to load system flows:', error);
    } finally {
      setSystemLoading(false);
    }
  };

  const loadPipelines = async () => {
    try {
      const data = await listDataflowPipelines({ scope: 'all', limit: 100 });
      setPipelines(data || []);
    } catch (error) {
      console.error('Failed to load pipelines:', error);
    }
  };

  const loadFlowDetail = async (id: string) => {
    if (isSystemFlowId(id)) {
      setCurrentFlow(null);
      const cached = matchSystemFlow(systemFlows, id);
      if (cached) {
        setSystemFlow(cached);
      }
      try {
        setLoading(true);
        const flows = await listSystemFlows(systemFlowListQuery(id));
        const normalized = (flows || []).map(normalizeSystemFlow);
        setSystemFlows(normalized);
        setSystemFlow(matchSystemFlow(normalized, id));
      } catch (error) {
        console.error('Failed to load system flow:', error);
        setSystemFlow(cached || null);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!appId) {
      setCurrentFlow(null);
      setSystemFlow(null);
      return;
    }
    try {
      setLoading(true);
      setSystemFlow(null);
      const flow = await getFlow(id);
      setCurrentFlow(flow);
    } catch (error) {
      console.error('Failed to load flow:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetLiveState = () => {
    setEventLog([]);
    setNodeStatus({});
    setEdgeStatus({});
    setEdgePulse({});
    setLogNodeFilter(null);
    setLiveDataEvents([]);
    setInfoPanelTab('info');
  };

  const appendEvent = (evt: FlowRunEvent) => {
    setEventLog((prev) => [evt, ...prev].slice(0, LOG_LIMIT));
    if (evt.ts) {
      setLastActivityAt(evt.ts);
    }
    if ((evt as any).kind === 'data' && (evt as any).status === 'mapped') {
      setLiveDataEvents((prev) => [{
        id: ++liveDataIdRef.current,
        raw: (evt as any).raw,
        iku: (evt as any).iku,
        endpoint_id: (evt as any).endpoint_id,
        ts: typeof evt.ts === 'string' ? Date.now() : (evt as any).ts || Date.now(),
      }, ...prev].slice(0, 50));
      setInfoPanelTab('livedata');
    }
    if (evt.node_id) {
      setNodeStatus((prev) => ({ ...prev, [evt.node_id as string]: evt }));
    }
    if (evt.source_id && evt.target_id) {
      const edgeId = edgeKeyLookup[`${evt.source_id}->${evt.target_id}`];
      if (edgeId) {
        setEdgeStatus((prev) => ({ ...prev, [edgeId]: evt }));
        setEdgePulse((prev) => ({ ...prev, [edgeId]: evt }));
        setTimeout(() => {
          setEdgePulse((prev) => {
            const next = { ...prev };
            delete next[edgeId];
            return next;
          });
        }, EDGE_PULSE_MS);
      }
    }
  };

  useEffect(() => {
    setActiveExecutionId(null);
    setActivePipelineRunId(null);
    if (dataflowStreamRef.current) {
      dataflowStreamRef.current.close();
      dataflowStreamRef.current = null;
    }
    if (pipelineStreamRef.current) {
      pipelineStreamRef.current.close();
      pipelineStreamRef.current = null;
    }
    resetLiveState();
  }, [flowId]);

  useEffect(() => {
    if (!activeExecutionId) return;
    if (dataflowStreamRef.current) {
      dataflowStreamRef.current.close();
      dataflowStreamRef.current = null;
    }
    const url = buildEventSourcePath(
      `/cp/dataflow/execute/stream/${encodeURIComponent(activeExecutionId)}`
    );
    if (!url) {
      setStreamState((prev) => ({ ...prev, dataflow: 'missing_token' }));
      setIsExecuting(false);
      return;
    }
    setStreamState((prev) => ({ ...prev, dataflow: 'connecting' }));
    const es = new EventSource(url);
    dataflowStreamRef.current = es;
    es.onopen = () => setStreamState((prev) => ({ ...prev, dataflow: 'connected' }));
    es.onerror = () => {
      setStreamState((prev) => ({ ...prev, dataflow: 'error' }));
      setIsExecuting(false);
    };
    es.addEventListener('execution_event', (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        appendEvent(data);
        // Detect terminal states
        if (data.kind === 'execution' && (data.status === 'completed' || data.status === 'failed')) {
          setIsExecuting(false);
        }
      } catch {
        // ignore
      }
    });
    getExecutionEvents(activeExecutionId)
      .then((events) => {
        events.forEach((evt) => appendEvent(evt as FlowRunEvent));
      })
      .catch(() => {});
    // Safety timeout: if execution doesn't complete within 60s, clear executing state
    const safetyTimer = setTimeout(() => {
      setIsExecuting(false);
    }, 60_000);
    return () => {
      es.close();
      clearTimeout(safetyTimer);
    };
  }, [activeExecutionId]);

  useEffect(() => {
    const pipelineId = (systemFlow?.metadata as any)?.pipeline_id || null;
    if (!pipelineId || !activePipelineRunId) return;
    if (pipelineStreamRef.current) {
      pipelineStreamRef.current.close();
      pipelineStreamRef.current = null;
    }
    setStreamState((prev) => ({ ...prev, pipeline: 'connecting' }));
    const url = buildEventSourcePath(
      `/cp/api-ingest/api/pipelines/${encodeURIComponent(pipelineId)}/events`,
      { run_id: activePipelineRunId }
    );
    if (!url) {
      setStreamState((prev) => ({ ...prev, pipeline: 'missing_token' }));
      setIsExecuting(false);
      return;
    }
    const es = new EventSource(url);
    pipelineStreamRef.current = es;
    es.onopen = () => {
      console.log('[DFM-SSE] pipeline stream connected');
      setStreamState((prev) => ({ ...prev, pipeline: 'connected' }));
    };
    es.onerror = (err) => {
      console.warn('[DFM-SSE] pipeline stream error, readyState:', es.readyState, err);
      setStreamState((prev) => ({ ...prev, pipeline: 'error' }));
      setIsExecuting(false);
      es.close();
      pipelineStreamRef.current = null;
    };
    es.addEventListener('pipeline_event', (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as FlowRunEvent;
        console.log('[DFM-SSE] pipeline_event:', data.kind, data.status, data.node_id || '', data.run_id || '');
        if (data.run_id && data.run_id !== activePipelineRunId) return;
        appendEvent(data);
        // Detect terminal states and stop executing indicator
        if (data.kind === 'pipeline' && (data.status === 'completed' || data.status === 'failed')) {
          console.log('[DFM-SSE] TERMINAL EVENT detected — clearing isExecuting');
          setIsExecuting(false);
        }
      } catch (parseErr) {
        console.warn('[DFM-SSE] parse error:', parseErr);
      }
    });
    // Safety timeout: if pipeline doesn't complete within 90s, clear executing state
    const safetyTimer = setTimeout(() => {
      console.log('[DFM-SSE] safety timeout fired — clearing isExecuting');
      setIsExecuting(false);
    }, 90_000);
    return () => {
      console.log('[DFM-SSE] pipeline SSE cleanup (effect re-run or unmount)');
      es.close();
      clearTimeout(safetyTimer);
    };
  }, [activePipelineRunId, systemFlow]);

  const handleCreateFlow = async (name: string, description: string) => {
    if (!appId) {
      alert('Select an app instance before creating a flow.');
      return;
    }
    try {
      const newFlow = await createFlow({
        name,
        description,
        nodes: [],
        edges: [],
        status: 'draft'
      });
      setFlows([newFlow, ...flows]);
      navigate(`/dataflow/${newFlow.id}`);
      setShowNewFlowDialog(false);
    } catch (error) {
      console.error('Failed to create flow:', error);
    }
  };

  const handleCreatePipeline = async () => {
    try {
      let spec: Record<string, any> = {};
      if (pipelineType === 'api_ingest') {
        spec = {
          api_id: pipelineApiId,
          endpoint_id: pipelineEndpointId,
          schedule: { every_seconds: Number(pipelineEverySeconds || 300) },
          ingest_mode: pipelineIngestMode,
          store_mode: pipelineStoreMode,
          enabled: true,
        };
      } else {
        spec = JSON.parse(pipelineSpecJson || '{}');
      }

      const created = await createDataflowPipeline({
        type: pipelineType,
        name: pipelineName || `${pipelineType} pipeline`,
        description: pipelineDescription,
        spec,
      });
      setPipelines([created, ...pipelines]);
      setShowPipelineDialog(false);
      setPipelineStep(0);
    } catch (error) {
      alert('Failed to create pipeline: ' + (error as Error).message);
    }
  };

  const handleExecuteFlow = async () => {
    const pipelineId = (systemFlow?.metadata as any)?.pipeline_id || null;
    if (pipelineId) {
      try {
        console.log('[DFM] handleExecuteFlow: starting pipeline run for', pipelineId);
        setIsExecuting(true);
        resetLiveState();
        const result = await runIngestPipeline(String(pipelineId));
        const runId = result?.run_id || null;
        console.log('[DFM] handleExecuteFlow: got run_id:', runId);
        setActivePipelineRunId(runId);
        appendEvent({
          source: 'api_ingest',
          kind: 'pipeline',
          status: 'started',
          ts: new Date().toISOString(),
          pipeline_id: String(pipelineId),
          run_id: runId || undefined,
          message: 'Pipeline run requested',
        });
        // Keep isExecuting true — the pipeline SSE useEffect will set it false
        // when a completed/failed event arrives, or after a safety timeout.
      } catch (error) {
        console.error('Failed to run pipeline:', error);
        alert('Failed to run pipeline: ' + (error as Error).message);
        setIsExecuting(false);
      }
      return;
    }
    if (systemFlow && !currentFlow) {
      const category = String((systemFlow.metadata as any)?.category || 'system');
      const message = category === 'iot_shack' || category === 'sensor' || category === 'sensor_type'
        ? 'This telemetry flow is a derived view only. It does not have a runnable pipeline attached.'
        : 'This system flow does not have a runnable pipeline attached.';
      appendEvent({
        source: 'dataflow',
        kind: 'execution',
        status: 'failed',
        ts: new Date().toISOString(),
        flow_id: systemFlow.id,
        message,
        detail: { category, runnable: false },
      });
      alert(message);
      return;
    }
    if (!currentFlow) return;

    try {
      setIsExecuting(true);
      resetLiveState();
      const result = await executeFlow(currentFlow.id);
      setActiveExecutionId(result.executionId);
      appendEvent({
        source: 'dataflow',
        kind: 'execution',
        status: 'started',
        ts: new Date().toISOString(),
        execution_id: result.executionId,
        flow_id: currentFlow.id,
        message: 'Flow execution requested',
      });
      // Keep isExecuting true — the dataflow SSE useEffect will set it false
      // when a completed/failed event arrives, or after a safety timeout.
    } catch (error) {
      console.error('Failed to execute flow:', error);
      alert('Failed to execute flow: ' + (error as Error).message);
      setIsExecuting(false);
    }
  };

  const handleDeleteSystemPipeline = async () => {
    const pipelineId = (systemFlow?.metadata as any)?.pipeline_id || null;
    if (!pipelineId) return;
    if (!confirm('Delete this ingest pipeline and remove its system flow? This cannot be undone.')) return;
    try {
      await deleteIngestPipeline(String(pipelineId));
      await loadSystemFlows();
      setSystemFlow(null);
      navigate('/dataflow');
    } catch (error) {
      console.error('Failed to delete pipeline:', error);
      alert('Failed to delete pipeline: ' + (error as Error).message);
    }
  };

  const handleDeleteFlow = async (id: string) => {
    if (!confirm('Are you sure you want to delete this flow?')) return;
    
    try {
      await deleteFlow(id);
      setFlows(flows.filter(f => f.id !== id));
      if (currentFlow?.id === id) {
        navigate('/dataflow');
        setCurrentFlow(null);
      }
    } catch (error) {
      console.error('Failed to delete flow:', error);
    }
  };

  const handleOpenEditFlow = () => {
    if (!currentFlow) return;
    setEditFlowName(currentFlow.name || '');
    setEditFlowDescription(currentFlow.description || '');
    setShowEditFlowDialog(true);
  };

  const handleSaveFlowEdit = async () => {
    if (!currentFlow) return;
    try {
      const updated = await updateFlow(currentFlow.id, {
        name: editFlowName,
        description: editFlowDescription,
      });
      setCurrentFlow(updated);
      setShowEditFlowDialog(false);
    } catch (error) {
      alert('Failed to update flow: ' + (error as Error).message);
    }
  };

  const handleOpenOverride = async () => {
    if (!systemFlow) return;
    try {
      const existing = await getSystemFlowOverride(systemFlow.id);
      setOverrideId(existing?._id || null);
      setOverrideName(existing?.name || systemFlow.name || '');
      setOverrideDescription(existing?.description || systemFlow.description || '');
      setOverrideNodesJson(JSON.stringify(existing?.nodes || systemFlow.nodes || [], null, 2));
      setOverrideEdgesJson(JSON.stringify(existing?.edges || systemFlow.edges || [], null, 2));
      setOverrideMetadataJson(JSON.stringify(existing?.metadata || systemFlow.metadata || {}, null, 2));
      setShowOverrideDialog(true);
    } catch (error) {
      alert('Failed to load override: ' + (error as Error).message);
    }
  };

  const handleSaveOverride = async () => {
    if (!systemFlow) return;
    try {
      const payload = {
        name: overrideName,
        description: overrideDescription,
        nodes: JSON.parse(overrideNodesJson || '[]'),
        edges: JSON.parse(overrideEdgesJson || '[]'),
        metadata: JSON.parse(overrideMetadataJson || '{}'),
      };
      if (overrideId) {
        await updateSystemFlowOverride(systemFlow.id, payload);
      } else {
        await createSystemFlowOverride(systemFlow.id, payload);
      }
      await loadSystemFlows();
      setShowOverrideDialog(false);
    } catch (error) {
      alert('Failed to save override: ' + (error as Error).message);
    }
  };

  const handleDeleteOverride = async () => {
    if (!systemFlow) return;
    if (!confirm('Delete override for this system flow?')) return;
    try {
      await deleteSystemFlowOverride(systemFlow.id);
      setOverrideId(null);
      await loadSystemFlows();
      setShowOverrideDialog(false);
    } catch (error) {
      alert('Failed to delete override: ' + (error as Error).message);
    }
  };

  // Live routing board (read layer) plus seat-scoped drag-create. Editor remains at /dataflow/:flowId.
  if (!flowId) {
    return <DataFlowRoutingBoard />;
  }

  // Flow editor view
  if (!appId && !systemFlow) {
    return (
      <div className="page-dataflow h-screen flex items-center justify-center bg-gray-50">
        <div className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
          Select an app instance to edit flows.
        </div>
      </div>
    );
  }
  const systemPipelineId = (systemFlow?.metadata as any)?.pipeline_id || null;
  const selectedSystemCategory = String((systemFlow?.metadata as any)?.category || '');
  const isRunnableSystemFlow = Boolean(systemFlow && systemPipelineId);
  const canExecuteSelectedFlow = Boolean(currentFlow || isRunnableSystemFlow);
  const nonRunnableSystemFlowReason = systemFlow && !systemPipelineId
    ? (selectedSystemCategory === 'iot_shack' || selectedSystemCategory === 'sensor' || selectedSystemCategory === 'sensor_type'
        ? 'This telemetry flow is a derived view only. It does not have a runnable pipeline attached.'
        : 'This system flow is read-only and does not have a runnable pipeline attached.')
    : null;
  const filteredEvents = logNodeFilter
    ? eventLog.filter((evt) => evt.node_id === logNodeFilter)
    : eventLog;
  return (
    <div className="page-dataflow h-screen flex flex-col">
      {/* Toolbar */}
      <div className="h-16 border-b flex items-center px-4 gap-4 bg-white">
        <button
          onClick={() => navigate('/dataflow')}
          className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded"
        >
          ← Back
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">
            {(systemFlow || currentFlow)?.name || 'Loading...'}
          </h1>
          <p className="text-xs text-gray-500">{(systemFlow || currentFlow)?.description}</p>
        </div>
        {lastActivityAt && (
          <div className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
            Live activity
          </div>
        )}
        <button
          onClick={handleExecuteFlow}
          disabled={isExecuting || !canExecuteSelectedFlow}
          title={nonRunnableSystemFlowReason || undefined}
          className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition ${
            isExecuting
              ? 'bg-amber-600 animate-pulse'
              : canExecuteSelectedFlow
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-400'
          }`}
        >
          {isExecuting
            ? '⏳ Executing...'
            : currentFlow
              ? '▶ Run Flow'
              : isRunnableSystemFlow
                ? '▶ Run Pipeline'
                : 'Pipeline Unavailable'}
        </button>
        {currentFlow && (
          <button
            onClick={handleOpenEditFlow}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Edit Flow
          </button>
        )}
        {systemFlow && (
          <button
            onClick={handleOpenOverride}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Edit Override
          </button>
        )}
        {systemFlow && systemPipelineId && (
          <button
            onClick={handleDeleteSystemPipeline}
            disabled={isExecuting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Delete Pipeline
          </button>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            Loading flow...
          </div>
        ) : (currentFlow || systemFlow) ? (
          <>
            {/* Canvas */}
            <div className="flex-1 relative bg-gray-50">
              <ReactFlowProvider>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={(_, node) => setLogNodeFilter(node.id)}
                  fitView
                >
                  <Background gap={16} size={1} />
                  <MiniMap />
                  <Controls />
                </ReactFlow>
              </ReactFlowProvider>
            </div>

            {/* Info panel */}
            <div className="w-96 border-l bg-white flex flex-col overflow-hidden">
              {/* Tab bar */}
              <div className="flex border-b">
                <button
                  type="button"
                  onClick={() => setInfoPanelTab('info')}
                  className={`flex-1 px-3 py-2 text-xs font-medium transition ${infoPanelTab === 'info' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Flow Info
                </button>
                <button
                  type="button"
                  onClick={() => setInfoPanelTab('livedata')}
                  className={`flex-1 px-3 py-2 text-xs font-medium transition ${infoPanelTab === 'livedata' ? 'border-b-2 border-green-600 text-green-700' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Live Data {liveDataEvents.length > 0 && <span className="ml-1 bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full text-[10px]">{liveDataEvents.length}</span>}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {/* Flow Info Tab */}
                {infoPanelTab === 'info' && (
                  <>
                    <h3 className="font-semibold mb-4">Flow Information</h3>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <p className="text-sm">{(systemFlow || currentFlow)?.name}</p>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <p className="text-sm text-gray-600">
                        {(systemFlow || currentFlow)?.description || 'No description'}
                      </p>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <span className={`inline-block px-2 py-1 text-xs rounded ${
                        (systemFlow || currentFlow)?.status === 'active' ? 'bg-green-100 text-green-700' :
                        (systemFlow || currentFlow)?.status === 'disabled' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {(systemFlow || currentFlow)?.status}
                      </span>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Statistics</label>
                      <div className="text-sm space-y-1">
                        <div>Nodes: {(systemFlow || currentFlow)?.nodes?.length || 0}</div>
                        <div>Connections: {(systemFlow || currentFlow)?.edges?.length || 0}</div>
                        <div>Executions: {currentFlow?.executionCount || 0}</div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Live execution</label>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Dataflow stream: {streamState.dataflow}</div>
                        <div>Pipeline stream: {streamState.pipeline}</div>
                        {logNodeFilter && (
                          <div className="pt-1">
                            <button
                              type="button"
                              onClick={() => setLogNodeFilter(null)}
                              className="text-xs text-blue-600 hover:text-blue-700"
                            >
                              Clear node filter ({logNodeFilter})
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 space-y-2 max-h-64 overflow-y-auto border border-gray-100 rounded p-2 bg-gray-50">
                        {filteredEvents.length === 0 ? (
                          <div className="text-xs text-gray-400">No live events yet.</div>
                        ) : (
                          filteredEvents.map((evt, index) => (
                            <div key={`${evt.ts}-${index}`} className="text-xs text-gray-700">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-400">{new Date(evt.ts).toLocaleTimeString()}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_STYLES[evt.status] || 'bg-gray-100 text-gray-600'}`}>
                                  {evt.status}
                                </span>
                                <span className="text-gray-600">{evt.kind}</span>
                                {evt.node_id && <span className="text-gray-500">{evt.node_id}</span>}
                              </div>
                              {evt.message && <div className="text-gray-600">{evt.message}</div>}
                              {evt.error && <div className="text-red-600">{evt.error}</div>}
                              {evt.detail && (
                                <div className="text-gray-500">{JSON.stringify(evt.detail)}</div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    {systemFlow && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Run capability</label>
                        <div className="text-sm text-gray-600">
                          {systemPipelineId
                            ? `Attached pipeline: ${String(systemPipelineId)}`
                            : (nonRunnableSystemFlowReason || 'This system flow is read-only.')}
                        </div>
                      </div>
                    )}
                    {currentFlow?.createdAt && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                        <p className="text-sm text-gray-600">
                          {new Date(currentFlow.createdAt).toLocaleString()}
                          {currentFlow.createdBy && ` by ${currentFlow.createdBy}`}
                        </p>
                      </div>
                    )}
                    <div className="pt-4 border-t">
                      {systemFlow ? (
                        <div className="text-xs text-gray-500 space-y-2">
                          {systemFlow.metadata?.category && (
                            <div>
                              <span className="font-medium text-gray-700">Category:</span>{' '}
                              {systemFlow.metadata.category}
                            </div>
                          )}
                          {systemFlow.metadata?.deviceId && !systemFlow.metadata?.imei && (
                            <div>
                              <span className="font-medium text-gray-700">Device:</span>{' '}
                              {String(systemFlow.metadata.deviceId)}
                            </div>
                          )}
                          {systemFlow.flow_type && (
                            <div>
                              <span className="font-medium text-gray-700">Flow type:</span>{' '}
                              {String(systemFlow.flow_type)}
                            </div>
                          )}
                          {systemFlow.entity?.type && (
                            <div>
                              <span className="font-medium text-gray-700">Entity type:</span>{' '}
                              {String(systemFlow.entity.type)}
                            </div>
                          )}
                          {systemFlow.entity?.id && (
                            <div>
                              <span className="font-medium text-gray-700">Entity ID:</span>{' '}
                              {String(systemFlow.entity.id)}
                            </div>
                          )}
                          {systemFlow.source?.system && (
                            <div>
                              <span className="font-medium text-gray-700">Source:</span>{' '}
                              {String(systemFlow.source.system)}
                            </div>
                          )}
                          {systemFlow.metadata?.deviceLabel && (
                            <div>
                              <span className="font-medium text-gray-700">Device:</span>{' '}
                              {String(systemFlow.metadata.deviceLabel)}
                            </div>
                          )}
                          {systemFlow.metadata?.imei && (
                            <div>
                              <span className="font-medium text-gray-700">IMEI:</span>{' '}
                              {String(systemFlow.metadata.imei)}
                            </div>
                          )}
                          {systemFlow.metadata?.measurementType && (
                            <div>
                              <span className="font-medium text-gray-700">Type:</span>{' '}
                              {String(systemFlow.metadata.measurementType)}
                            </div>
                          )}
                          {lastActivityAt && (
                            <div>
                              <span className="font-medium text-gray-700">Last activity:</span>{' '}
                              {new Date(lastActivityAt).toLocaleString()}
                            </div>
                          )}
                          {(systemFlow as any).override?.id && (
                            <div>
                              <span className="font-medium text-gray-700">Override:</span>{' '}
                              Active
                            </div>
                          )}
                          <div className="pt-2 text-gray-400">
                            This flow is auto-generated from backend signals.
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">
                          Drag-and-drop editing and advanced controls are coming next.
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Live Data Tab */}
                {infoPanelTab === 'livedata' && (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-sm">Live Data Stream</h3>
                      {liveDataEvents.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setLiveDataEvents([])}
                          className="text-xs text-gray-500 hover:text-red-600"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {liveDataEvents.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-3xl mb-2 text-gray-300">&#x21C4;</div>
                        <p className="text-sm text-gray-500">No data events yet</p>
                        <p className="text-xs text-gray-400 mt-1">Run a pipeline to see raw vs mapped data side-by-side</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {liveDataEvents.map((evt) => (
                          <LiveDataCard key={evt.id} event={evt} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-1 text-center px-6">
            <div>Flow not found</div>
            {flowId && isSystemFlowId(flowId) && (
              <p className="text-xs text-gray-400">
                Looked up {canonicalizeSystemFlowRouteId(flowId)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

